import { lazy, Suspense, useCallback, useState, type FormEvent, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { KeyRound, Loader2, QrCode, XCircle } from "lucide-react";
import useConfig from "@/hooks/useConfig";
import { V2Shell } from "@/v2/components/V2Shell";
import { Button } from "@/v2/components/ui/button";
import { Input } from "@/v2/components/ui/input";
import { loadAudienceLayerModule } from "@/lib/audienceLayerRemote";
import {
  useSecondScreenHost,
  type RedeemedSecondScreenSession,
  type SecondScreenHostContext,
} from "./useSecondScreenHost";

// Loaded at runtime from the separate Audience Layer deploy, through the same
// loader and the same lazy/ErrorBoundary/Suspense shape as
// src/components/AudienceLayerPanel.tsx: a remote load or runtime failure is
// contained here rather than blanking the whole screen a fan just scanned a QR
// code to reach. The exposed name is a string rather than a bare specifier
// because nothing in the build graph knows the remote exists (see
// lib/audienceLayerRemote).
const AudienceLayer = lazy(() =>
  loadAudienceLayerModule<{
    default: React.ComponentType<{ hostContext?: SecondScreenHostContext }>;
  }>("App"),
);

/**
 * `#s=<sessionId>` off the phone's own QR-scanned link. Read once on mount: a
 * fragment never reaches the server and React Router never sees it, so this
 * is the only place that ever looks at it.
 */
function readSessionIdFromHash(): string | null {
  const match = /(?:^|[#&])s=([^&]*)/.exec(window.location.hash);
  const id = match?.[1];
  return id ? decodeURIComponent(id) : null;
}

type Step = "needs-session" | "pin" | "connecting" | "ready" | "error";

/**
 * The phone's route: reads the session id a QR code put in the URL fragment,
 * takes the PIN shown on the bigger screen, redeems it, and mounts the
 * Audience Layer full screen. Public on purpose - the whole point is that the
 * phone has no tenant session of its own - and wrapped in V2Shell so the
 * layer renders in the tenant's brand instead of the widget's stock blue.
 */
export default function SecondScreen() {
  const [sessionId] = useState(readSessionIdFromHash);
  const [step, setStep] = useState<Step>(sessionId ? "pin" : "needs-session");
  const [pin, setPin] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [session, setSession] = useState<RedeemedSecondScreenSession | null>(null);

  const config = useConfig();
  const tenantName = config?.subtitle || config?.title || "this game";
  const apiBaseUrl = import.meta.env.VITE_AUDIENCE_LAYER_API_URL as string | undefined;

  // The session's own stream reports this mid-"ready": the fan hit
  // Disconnect on the bigger screen, or the grace period lapsed while this
  // phone was away. Either way the layer stops being this phone's to show.
  const handleRevoked = useCallback(() => {
    setSession(null);
    setMessage("This phone was disconnected. Scan the QR code again to reconnect.");
    setStep("error");
  }, []);

  const submitPin = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!sessionId) return;

      setStep("connecting");
      setMessage(null);

      try {
        const res = await fetch(`${apiBaseUrl ?? ""}/second-screen/${sessionId}/redeem`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pin }),
        });

        if (res.ok) {
          const body = await res.json();
          setSession({
            sessionId,
            token: body.token,
            gameId: body.gameId,
            tenant: body.tenant,
            user: body.user,
          });
          setStep("ready");
          return;
        }

        // 410: the code's ten minute window is over. 409: this code has
        // already been redeemed, once, by whichever phone got there first.
        // A reload of this exact page is one of the ways to land here too -
        // the token this phone was handed lives only in memory, so losing it
        // and re-redeeming the same fragment looks identical to the API as
        // a second phone trying the same code - which is why this does not
        // claim it was "another phone": that would be a guess this route has
        // no way to actually confirm. Neither status is fixed by trying the
        // same PIN again, so both are terminal - no form, no Connect button
        // - and the fan's only way forward is a fresh QR code from the
        // bigger screen.
        if (res.status === 410) {
          setMessage("This code has expired. Ask for a new one on the bigger screen.");
          setStep("error");
          return;
        }
        if (res.status === 409) {
          setMessage(
            "This code has already been used to connect a phone. If that was this phone and reloading brought you back here, ask for a new QR code on the bigger screen: this one will not reconnect it.",
          );
          setStep("error");
          return;
        }

        // A wrong PIN (401) and anything else this build does not
        // specifically recognize both just mean: look again and retry.
        setMessage("That PIN did not work. Check it and try again.");
        setStep("pin");
      } catch {
        setMessage("Could not reach the server. Check your connection and try again.");
        setStep("pin");
      }
    },
    [sessionId, pin, apiBaseUrl],
  );

  if (step === "ready" && session) {
    return (
      <V2Shell>
        {/* h-dvh, not h-screen: h-screen is 100vh, and on a phone that is the
            LARGE viewport, the one that assumes the browser's own address
            bar/toolbar is hidden. With it, the bottom strip (the layer's own
            message composer) sat under the toolbar with nothing able to
            scroll to it, so a fan could not type. h-dvh tracks the viewport
            the browser chrome actually leaves behind. */}
        <div className="h-dvh w-full overflow-hidden bg-background">
          <AudienceLayerMount session={session} apiBaseUrl={apiBaseUrl} onRevoked={handleRevoked} />
        </div>
      </V2Shell>
    );
  }

  return (
    <V2Shell>
      {/* V2Shell locks body to height:100vh;overflow:hidden, so this page owns
          its own scroll container, the same fix RedeemGift uses for its own
          plain card states. h-dvh for the same reason as the "ready" branch
          above: h-screen (100vh) is the phone's large viewport, which sits
          under the browser's own toolbar. */}
      <div className="h-dvh w-full overflow-y-auto bg-muted/40">
        <div className="min-h-full flex items-center justify-center" style={{ padding: "16px" }}>
          <div
            className="bg-card border border-border rounded-2xl shadow-sm w-full max-w-[400px] text-center"
            style={{ padding: "32px" }}
          >
            {step === "needs-session" && <NeedsSessionCard tenantName={tenantName} />}
            {(step === "pin" || step === "connecting") && (
              <PinCard
                pin={pin}
                onPinChange={setPin}
                onSubmit={submitPin}
                error={step === "pin" ? message : null}
                submitting={step === "connecting"}
                tenantName={tenantName}
              />
            )}
            {step === "error" && <TerminalCard message={message ?? "Something went wrong."} />}
          </div>
        </div>
      </div>
    </V2Shell>
  );
}

function IconBadge({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex items-center justify-center rounded-full bg-muted"
      style={{ width: 56, height: 56, margin: "0 auto" }}
    >
      {children}
    </div>
  );
}

function NeedsSessionCard({ tenantName }: { tenantName: string }) {
  return (
    <div className="flex flex-col" style={{ gap: "12px" }}>
      <IconBadge>
        <QrCode className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
      </IconBadge>
      <h1 className="text-lg font-semibold text-foreground">Connect Your Phone</h1>
      <p className="text-sm text-muted-foreground">
        Scan the QR code shown on the bigger screen to connect this phone to{" "}
        {tenantName}&apos;s Audience Layer.
      </p>
    </div>
  );
}

type PinCardProps = {
  pin: string;
  onPinChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  error: string | null;
  submitting: boolean;
  tenantName: string;
};

function PinCard({ pin, onPinChange, onSubmit, error, submitting, tenantName }: PinCardProps) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col" style={{ gap: "16px" }}>
      <div className="flex flex-col" style={{ gap: "12px" }}>
        <IconBadge>
          <KeyRound className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
        </IconBadge>
        <h1 className="text-lg font-semibold text-foreground">Enter the PIN</h1>
        <p className="text-sm text-muted-foreground">
          Type the 4-digit PIN shown on the bigger screen to connect this phone to{" "}
          {tenantName}&apos;s Audience Layer.
        </p>
      </div>

      <div className="text-left">
        <label htmlFor="second-screen-pin" className="sr-only">
          PIN
        </label>
        {/* The border lives on this wrapper, not on the input: v2.css's
            unlayered `border-width: 0` preflight replacement outranks any
            border utility on an <input>, which is why the header search and
            the auth fields wrap theirs the same way. Focus shows here too, so
            a locked phone screen coming back still shows where the cursor is. */}
        <div className="rounded-md border border-input bg-transparent focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
          <Input
            id="second-screen-pin"
            className="border-0 text-center text-lg tracking-[0.5em] shadow-none focus-visible:ring-0"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            autoComplete="one-time-code"
            placeholder="0000"
            value={pin}
            disabled={submitting}
            onChange={(event) => onPinChange(event.target.value.replace(/[^0-9]/g, ""))}
          />
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert" style={{ marginTop: "8px" }}>
            {error}
          </p>
        )}
      </div>

      <Button
        type="submit"
        disabled={submitting || pin.length === 0}
        className="ring-1 ring-inset ring-border"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        Connect
      </Button>
    </form>
  );
}

function TerminalCard({ message }: { message: string }) {
  return (
    <div className="flex flex-col" style={{ gap: "12px" }}>
      <IconBadge>
        <XCircle className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
      </IconBadge>
      <h1 className="text-lg font-semibold text-foreground">Can&apos;t Connect</h1>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function FullScreenLoader() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
    </div>
  );
}

function AudienceLayerMount({
  session,
  apiBaseUrl,
  onRevoked,
}: {
  session: RedeemedSecondScreenSession;
  apiBaseUrl?: string;
  onRevoked: () => void;
}) {
  const hostContext = useSecondScreenHost({ session, apiBaseUrl, onRevoked });

  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div className="flex h-full w-full items-center justify-center text-center" style={{ padding: "24px" }}>
          <p className="text-sm text-muted-foreground">
            The Audience Layer could not load. Reload this page to try again.
          </p>
        </div>
      )}
      onError={(err) => console.error("[second-screen] runtime error:", err)}
    >
      <Suspense fallback={<FullScreenLoader />}>
        <AudienceLayer hostContext={hostContext} />
      </Suspense>
    </ErrorBoundary>
  );
}
